/**
 * @fileoverview Servicio de lógica de negocio para anulación de SOAT.
 * Centraliza validaciones, transformaciones y reglas de negocio del flujo de anulación,
 * evitando que esta lógica quede dispersa en el componente FormalitiesSOATCancellation.vue.
 * @module services/soat-cancellation.service
 */

import { FORMALITY_TYPES, FORMALITY_STATES, RESPONSE_MEDIUMS } from '@/constants'
import { 
  validateEmail, 
  validateTextLength, 
  validateInList, 
  validatePolicyNumber,
  sanitizeText 
} from '@/utils/formValidation'
import { SOAT_CANCELLATION_REASONS } from '@/constants'

/**
 * Inicializa el formulario de anulación con datos del usuario.
 * @param {Object} basicInfo - Información básica del usuario desde Vuex
 * @param {string} policyNumber - Número de póliza SOAT
 * @returns {Object} Formulario inicializado con valores del usuario
 */
export function initForm(basicInfo, policyNumber) {
  if (!basicInfo) {
    return createEmptyForm(policyNumber)
  }

  return {
    policy: policyNumber || '',
    product: 'SOAT',
    transact: FORMALITY_TYPES.CANCELLATION,
    email: basicInfo.correo || '',
    cellphone: basicInfo.celular === '' ? '-' : basicInfo.celular || '',
    phone: basicInfo.telefono === '' ? '-' : basicInfo.telefono || '',
    name: basicInfo.nombres || '',
    paternal_surname: basicInfo.apellidoPaterno || '',
    maternal_surname: basicInfo.apellidoMaterno || '',
    type_document: basicInfo.tipoDocumento || 'DNI',
    number_document: basicInfo.numeroDocumento || '',
    home_address: '',
    interior: '',
    response_medium: null,
    department: null,
    province: null,
    district: null,
    detail_transact: '',
    state: FORMALITY_STATES.PENDING,
    cancellation_reason: ''
  }
}

/**
 * Crea un formulario vacío con valores por defecto.
 * @param {string} policyNumber - Número de póliza SOAT
 * @returns {Object} Formulario vacío
 */
function createEmptyForm(policyNumber) {
  return {
    policy: policyNumber || '',
    product: 'SOAT',
    transact: FORMALITY_TYPES.CANCELLATION,
    name: '',
    paternal_surname: '',
    maternal_surname: '',
    type_document: 'DNI',
    number_document: '',
    home_address: '',
    interior: '',
    response_medium: null,
    department: null,
    province: null,
    district: null,
    cellphone: '',
    phone: null,
    email: '',
    detail_transact: '',
    state: FORMALITY_STATES.PENDING,
    cancellation_reason: ''
  }
}

/**
 * Prepara el payload para el endpoint v2 de requerimientos.
 * Sanitiza y valida los datos antes de enviarlos.
 * @param {Object} form - Formulario del componente Vue
 * @returns {Object} Payload formateado para /api-zonasegura/v2/requerimientos/registrar
 */
export function prepareDataV2(form) {
  const sanitizedForm = sanitizeFormData(form)
  
  return {
    policy: sanitizedForm.policy,
    product: sanitizedForm.product,
    transact: sanitizedForm.transact,
    name: sanitizedForm.name,
    paternal_surname: sanitizedForm.paternal_surname,
    maternal_surname: sanitizedForm.maternal_surname,
    type_document: sanitizedForm.type_document || 'DNI',
    number_document: sanitizedForm.number_document,
    email: sanitizedForm.email,
    cellphone: sanitizedForm.cellphone || '-',
    phone: sanitizedForm.phone || sanitizedForm.cellphone || '-',
    response_medium: RESPONSE_MEDIUMS.ELECTRONIC,
    description_cancellation: sanitizedForm.cancellation_reason || '-',
    detail_transact: '-',
    cancellation_reason: '-',
    enviar_correo: true
  }
}

/**
 * Valida que el formulario tenga los campos mínimos requeridos y cumplan con los formatos.
 * @param {Object} form - Formulario a validar
 * @returns {Object} Objeto con isValid y errors
 * 
 * @example
 * const { isValid, errors } = validateForm(formData)
 * if (!isValid) {
 *   errors.forEach(error => console.error(error))
 * }
 */
export function validateForm(form) {
  const allErrors = []

  // Validar número de póliza
  const policyValidation = validatePolicyNumber(form.policy)
  if (!policyValidation.isValid) {
    allErrors.push(...policyValidation.errors)
  }

  // Validar email
  const emailValidation = validateEmail(form.email)
  if (!emailValidation.isValid) {
    allErrors.push(...emailValidation.errors)
  }

  // Validar motivo de anulación
  const reasonValidation = validateInList(
    form.cancellation_reason, 
    SOAT_CANCELLATION_REASONS, 
    'Motivo de anulación'
  )
  if (!reasonValidation.isValid) {
    allErrors.push(...reasonValidation.errors)
  }

  // Validar longitud del motivo
  const reasonLengthValidation = validateTextLength(
    form.cancellation_reason, 
    1000, 
    'Motivo de anulación'
  )
  if (!reasonLengthValidation.isValid) {
    allErrors.push(...reasonLengthValidation.errors)
  }

  // Validar número de documento
  if (!form.number_document) {
    allErrors.push('Número de documento requerido')
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  }
}

/**
 * Sanitiza los datos del formulario antes de enviarlos.
 * @param {Object} form - Formulario a sanitizar
 * @returns {Object} Formulario con datos sanitizados
 */
export function sanitizeFormData(form) {
  return {
    ...form,
    policy: sanitizeText(form.policy, 50),
    email: sanitizeText(form.email, 100),
    cancellation_reason: sanitizeText(form.cancellation_reason, 1000),
    name: sanitizeText(form.name, 100),
    paternal_surname: sanitizeText(form.paternal_surname, 100),
    maternal_surname: sanitizeText(form.maternal_surname, 100),
    number_document: sanitizeText(form.number_document, 20)
  }
}



// WEBPACK FOOTER //
// ./src/services/soat-cancellation.service.js